import isFunction from 'lodash/isFunction'
import type { MouseEventHandler, ReactNode } from 'react'
import React, { useCallback, useMemo } from 'react'
import type { DragSortItemProps } from '@ztechapps/webui.util'
import type { OnRowSelectFunction } from './index'

export type RowConfig<T = any> = {
  onSelect?: OnRowSelectFunction<T>
  highlightClassName: string
  index: number
  key: string
  dragProps?: DragSortItemProps
  dragRowClassName?: string
}

export type RowProps<T = any> = {
  _id?: string
  children?: ReactNode
  onClick?: React.MouseEventHandler<HTMLTableRowElement>
  className?: string
  style?: React.CSSProperties
  _config?: RowConfig<T>
  onMouseOver?: MouseEventHandler<HTMLTableRowElement>
  onMouseLeave?: MouseEventHandler<HTMLTableRowElement>
} & T

function RowComponent<T = any>({
  _id,
  children,
  onClick,
  className,
  style,
  _config,
  onMouseOver,
  onMouseLeave,
  ...rest
}: RowProps<T>) {
  const { onSelect, highlightClassName, dragProps, dragRowClassName } = _config ?? {}

  const _onClick = useCallback(
    (e) => {
      if (onClick) return onClick(e)
      onSelect?.({ _id, _config, ...(rest as T) })
    },
    [onClick, onSelect, _id, _config, rest]
  )

  const combinedClassName = useMemo(
    () => [highlightClassName ?? '', className ?? '', dragRowClassName ?? ''].filter(Boolean).join(' '),
    [highlightClassName, className, dragRowClassName]
  )

  const renderedChildren = useMemo(
    () => (isFunction(children) ? children({ _id, _config, ...rest }) : children),
    [children, _id, _config, rest]
  )

  return (
    <tr
      {...dragProps}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      className={combinedClassName}
      onClick={_onClick}
      style={style}
    >
      {renderedChildren}
    </tr>
  )
}

export const Row = React.memo(RowComponent) as typeof RowComponent
